<template>
  <AdminPageView :loading :actions-only-on-top="true" :title="'Événements'" :loadingErrorMessage>
    <template #actions>
      <router-link :to="{ name: 'admin-new-travel' }">
        <button class="btn btn-outline-success text-nowrap">
          <i class="fa-solid fa-plus"></i> Ajouter<span class="d-none d-md-inline-block">&nbsp;un événement</span></button>
      </router-link>
    </template>
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">Evénements à venir</h5>
      </div>
      <div class="card-body">

        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Nom</th>
                <!-- <th>Nuitées</th> -->
                <th>Type</th>
                <th class="text-nowrap">Date début</th>
                <th class="text-nowrap">Date fin</th>
                <th class="text-center text-nowrap">Inscriptions en cours</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="travel in currentTravels" :key="travel.id">
                <td class="py-0 text-nowrap">
                  <router-link :to="{ name:'travel-board-by-id', params: { id: travel.id } }" class="btn btn-link px-2 link-underline link-underline-opacity-0">
                    <i class="fa-solid fa-gauge"></i> {{ travel.name }}
                  </router-link>
                </td>
                <!-- <td class="ms-3">{{ travel.nights }}</td> -->
                <td>{{ GetTravelTypeLabel(travel.type) }}</td>
                <td>{{ formatDate(travel.startDate,'DD/MM/YYYY') }}</td>
                <td>{{ formatDate(travel.endDate,'DD/MM/YYYY') }}</td>
                <td class="text-center">
                  <span v-if="isRegistrationOpen(travel)" class="text-success">Oui</span>
                  <span v-else class="text-body">Non</span>
                </td>
                <td class="py-0 text-center text-nowrap">
                  <router-link :to="{ name:'admin-edit-travel', params: { id: travel.id } }" class="btn btn-link px-2" title="Modifier l'événement">
                    <i class="fa-solid fa-pencil"></i>
                  </router-link>
                  <router-link :to="{ name:'admin-edit-travel-participants', params: { id: travel.id } }" class="btn btn-link px-2" title="Gérer les participants">
                    <i class="fa-solid fa-people-group"></i>
                  </router-link>
                  <router-link :to="{ name:'admin-travel-export', params: { id: travel.id } }" class="btn btn-link px-2" title="Exporter les données">
                    <i class="fa-solid fa-file-export"></i>
                  </router-link>
                  <button class="btn btn-link pe-2 ms-3" @click="deleteTravel(travel.id)" title="Supprimer l'événement"><i class="fa-regular fa-trash-can"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card mt-3" v-if="pastTravels.length > 0">
      <div class="card-header">
        <h5 class="mb-0">Evénements passés</h5>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Nom</th>
                <!-- <th>Nuitées</th> -->
                <th>Type</th>
                <th class="text-nowrap">Date début</th>
                <th class="text-nowrap">Date fin</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="travel in pastTravels.sort((a, b) => b.startDate.getTime() - a.startDate.getTime())" :key="travel.id">
                <td class="py-0 text-nowrap">
                  <router-link :to="{ name:'travel-board-by-id', params: { id: travel.id } }" class="btn btn-link px-2 link-underline link-underline-opacity-0">
                    <i class="fa-solid fa-gauge"></i> {{ travel.name }}
                  </router-link>
                </td>
                <!-- <td class="ms-3">{{ travel.nights }}</td> -->
                <td>{{ GetTravelTypeLabel(travel.type) }}</td>
                <td>{{ formatDate(travel.startDate,'DD/MM/YYYY') }}</td>
                <td>{{ formatDate(travel.endDate,'DD/MM/YYYY') }}</td>
                <td class="py-0 text-center text-nowrap">
                  <router-link title="Modifier l'événement" :to="{ name:'admin-edit-travel', params: { id: travel.id } }" class="btn btn-link px-2">
                    <i class="fa-solid fa-pencil"></i>
                  </router-link>
                  <router-link title="Gérer les participants" :to="{ name:'admin-edit-travel-participants', params: { id: travel.id } }" class="btn btn-link px-2">
                    <i class="fa-solid fa-people-group"></i>
                  </router-link>
                  <router-link title="Exporter les données" :to="{ name:'admin-travel-export', params: { id: travel.id } }" class="btn btn-link px-2">
                    <i class="fa-solid fa-file-export"></i>
                  </router-link>
                  <button title="Supprimer" class="btn btn-link pe-2 ms-3" @click="deleteTravel(travel.id)"><i class="fa-regular fa-trash-can"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AdminPageView>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import adminApi from '@/api/adminApi'
import AdminPageView from '@/views/admin/AdminPageView.vue';
import { useNotificationsStore } from '@/stores/notificationStore';

import { useConfirmDialog, GetTravelTypeLabel,formatDate } from '@/compositions/helper';
const { confirmDialog } = useConfirmDialog();

const notificationsStore = useNotificationsStore();

const loading = ref(true);
const loadingErrorMessage = ref<string>()
const travels = ref<AdminTravelBasic[]>([]);

const currentTravels = computed(() => {
  return travels.value.filter(travel => travel.endDate >= new Date());
});

const pastTravels = computed(() => {
  return travels.value.filter(travel => travel.endDate < new Date());
});

const isRegistrationOpen = (travel: AdminTravelBasic): boolean => {
  const now = new Date();
  return travel.registrationStartDate <= now && travel.registrationEndDate >= now && travel.endDate >= now;
};

onMounted(() => {
  adminApi.getTravels().then((response) => {
    travels.value = response.data.items;
  }).catch((error: Error) => {
    loadingErrorMessage.value = 'Impossible de charger les événements';
    notificationsStore.notifyError('Erreur lors de la récupération des événements', error.message);
  }).finally(() => {
    loading.value = false;
  });
});

const deleteTravel = async (id: number) => {
  if(await confirmDialog(`Êtes-vous sûr de vouloir supprimer l'événement "${travels.value.find(travel => travel.id === id)?.name}" ?`)) {
    adminApi.deleteTravel(id).then(() => {
      travels.value = travels.value.filter(travel => travel.id !== id);
      notificationsStore.notifySuccess("Événement supprimé", "L'événement a été supprimé avec succès.");
    }).catch((error: Error) => {
      notificationsStore.notifyError("Erreur lors de la suppression de l'événement.", error.message);
  });
  }
};

</script>
